import React, { Component } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { TransitionMotion, spring } from 'react-motion'
import { triangle, stripUnit } from 'polished'
import { media } from '../theme'
import Button from './Button'
import ButtonArrow from './ButtonArrow'
import IconCloseDarkImage from '../assets/images/icon--close-dark.svg'
import CharacterPluggyTipImage from '../assets/images/character--pluggy-tip@3x.png'
import CharacterKeoniTipImage from '../assets/images/character--keoni-tip@3x.png'

const Container = styled.div.attrs({
  style: ({ opacity = 1, translateY = 0 }) => ({
    opacity,
    transform: `translateY(${translateY}%)`,
  }),
})`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  position: relative;
`

const Character = styled.img`
  height: ${props => props.theme.spacing(5.5)};
  margin-bottom: calc(0rem - ${props => props.theme.spacing(5)} / 2);
  position: relative;
  z-index: 1;
`

const boxPaddingBottomChooser = ({ theme, closable, closeType }) => {
  if (!closable) return 'auto'

  switch (closeType) {
    case 'button':
    case 'buttonArrow':
      return theme.spacing(3)

    case 'close':
    default:
      return theme.spacing(2)
  }
}

const Box = styled.div`
  background: linear-gradient(
    0deg,
    ${props => props.theme.color.Tip.bg.join(',')}
  );
  border-radius: ${props => props.theme.spacing(-1)};
  font-family: ${props => props.theme.fontFamily.primary};
  padding: ${props => props.theme.spacing(0)};
  padding-bottom: ${boxPaddingBottomChooser};
  padding-top: ${props =>
    props.character
      ? `calc(${props.theme.spacing(0)} + ${props.theme.spacing(5)} / 2)`
      : 'auto'};
  position: relative;
  width: 100%;

  ${media.greaterThan('medium')`
    padding-bottom: ${props => props.theme.spacing(0)};

    &:after {
      bottom: 0;
      content: '';
      display: block;
      height: 0;
      left: 50%;
      position: absolute;
      transform: translate(-50%, 99%);
      width: 2rem;
      ${props =>
        triangle({
          pointingDirection: 'bottom',
          width: stripUnit(props.theme.spacing(1.5)) * 16,
          height: stripUnit(props.theme.spacing(1)) * 16,
          foregroundColor: props.theme.color.Tip.bg[0],
        })};
    }
  `};
`

const Heading = styled.h5`
  color: ${props => props.theme.color.Tip.copySecondary};
  font-size: ${props => props.theme.fontSize(2)};
  letter-spacing: 0.05rem;
  line-height: 1;
  margin-bottom: ${props => props.theme.spacing(-3)};
  text-transform: uppercase;
`

const Content = styled.div`
  color: ${props => props.theme.color.copy};
  font-size: ${props => props.theme.fontSize(1.5)};
  line-height: 1;

  ${media.greaterThan('medium')`
  font-size: ${props => props.theme.fontSize(1.3)};
  `};

  ${media.greaterThan('large')`
  font-size: ${props => props.theme.fontSize(1.5)};
  `};

  p {
    font-size: inherit;
    font-weight: inherit;
    margin-bottom: ${props => props.theme.spacing(-1)};

    &:last-child {
      margin-bottom: 0;
    }
  }

  em {
    color: ${props => props.theme.color.limeGreen};
    text-transform: uppercase;
  }

  .gatsby-resp-image-wrapper {
    margin: ${p => p.theme.spacing(1)} 0 ${p => p.theme.spacing(-2)};
    width: 50%;
  }
`

const CloseContainer = styled.div`
  align-items: center;
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  position: absolute;
  right: 0;
  transform: translateY(50%);

  ${media.greaterThan('medium')`
    bottom: auto;
    left: auto;
    right: 0;
    top: 0;
    transform: translate(40%, -40%);
  `};
`

const CloseButton = styled.div`
  background-color: #e1e8e7;
  border-radius: 50%;
  cursor: pointer;
  height: ${props => props.theme.spacing(1.75)};
  overflow: hidden;
  padding: ${props => props.theme.spacing(-2.25)};
  position: relative;
  width: ${props => props.theme.spacing(1.75)};
  box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.3);

  &::before {
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.7),
      rgba(255, 255, 255, 0)
    );
    bottom: 0;
    content: '';
    display: block;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
  }
`

const StyledButton = styled(Button)`
  margin-top: ${p => p.theme.spacing(0)};
`

/**
 * Display a message to the user. The message be customized to show a
 * character. It can optionally be closed by providing a **close** handler.
 */
export default class Tip extends Component {
  static propTypes = {
    /** Heading shown above the content. */
    heading: PropTypes.string,

    /** Display a character. */
    character: PropTypes.oneOf(['pluggy', 'keoni']),

    /** Allow the tip to be closed. */
    closable: PropTypes.bool,

    /** Function called when clicking the close button. */
    close: PropTypes.func,

    /** Text to display for closing. Only used when closeType is "button". */
    closeButtonText: PropTypes.string,

    /** URL to route for closing. Only used when closeType is "button". */
    closeButtonTo: PropTypes.string,

    /** Type of UI for closing. */
    closeType: PropTypes.oneOf(['close', 'button', 'buttonArrow']),
  }

  static defaultProps = {
    closable: false,
    close: () => {},
    closeButtonText: 'Close',
    closeButtonTo: '#',
    closeType: 'close',
  }

  /**
   * Generate a react-motion compatible defaultStyles object. Used to
   * transition the tip.
   */
  genDefaultStyles() {
    return [{ key: 'tip', style: { opacity: 0, translateY: 20 } }]
  }

  /**
   * Generate a react-motion compatible styles object. Used to transition the
   * tip.
   */
  genStyles() {
    return [
      {
        key: 'tip',
        style: {
          opacity: spring(1, { stiffness: 200 }),
          translateY: spring(0),
        },
      },
    ]
  }

  getCharacterSrc() {
    const { character } = this.props

    switch (character) {
      case 'pluggy':
        return CharacterPluggyTipImage

      case 'keoni':
        return CharacterKeoniTipImage

      default:
        return false
    }
  }

  renderClose() {
    const { close, closeType, closeButtonText, closeButtonTo } = this.props

    switch (closeType) {
      case 'button':
        return (
          <Button pulse={true} onClick={close} to={closeButtonTo}>
            {closeButtonText}
          </Button>
        )

      case 'buttonArrow':
        return (
          <ButtonArrow
            size="small"
            animateSideToSide={true}
            onClick={close}
            to={closeButtonTo}
          >
            {closeButtonText}
          </ButtonArrow>
        )

      case 'close':
        return (
          <CloseButton onClick={close}>
            <img src={IconCloseDarkImage} />
          </CloseButton>
        )
    }

    return
  }

  render() {
    const {
      character,
      children,
      closable,
      closeType,
      heading,
      ...props
    } = this.props
    const characterSrc = this.getCharacterSrc()

    return (
      <TransitionMotion
        defaultStyles={this.genDefaultStyles()}
        styles={this.genStyles()}
      >
        {interpolatingStyles => (
          <Container
            opacity={interpolatingStyles[0].style.opacity}
            translateY={interpolatingStyles[0].style.translateY}
            {...props}
          >
            {character && <Character src={characterSrc} />}
            <Box
              character={character}
              closable={closable}
              closeType={closeType}
            >
              {heading && <Heading>{heading}</Heading>}
              <Content>{children}</Content>
              {closable && (
                <CloseContainer>{this.renderClose()}</CloseContainer>
              )}
            </Box>
          </Container>
        )}
      </TransitionMotion>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/Tip.js